import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import Dialog from "@mui/material/Dialog";
import styled from "@emotion/styled";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

import {
  getCart,
  updateCart,
  removeItem,
  clearCart,
} from "../../actions/cartAction";
import InputQuantityCom from "../Helpers/InputQuantityCom";
import PrimaryButton from "../Helpers/Buttons/PrimaryButton";
import SecondaryButton from "../Helpers/Buttons/SecondaryButton";

const StyledDialogContent = styled(DialogContent)`
  text-align: center;
  padding: 30px;
`;

const StyledDialogActions = styled(DialogActions)`
  display: flex;
  justify-content: space-evenly;
  padding: 15px;
`;

// Safely convert values to number.
// Handles null, undefined, string numbers, and invalid values.
const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

// Format money safely.
const formatMoney = (value) => {
  return toNumber(value).toFixed(2);
};

// Always treat discountRate as a positive rate.
// Supports both old data (-35) and new data (35 or "35.00").
const getDiscountRate = (item) => {
  return Math.abs(toNumber(item?.discountRate));
};

const hasDiscount = (item) => {
  return getDiscountRate(item) > 0;
};

// Calculate discounted unit price.
// Prefer backend discountedItemPrice if it exists.
// Otherwise calculate from itemPrice and discountRate.
const calculateDiscountedItemPrice = (item) => {
  const itemPrice = toNumber(item?.itemPrice);
  const discountedItemPrice = toNumber(item?.discountedItemPrice);
  const discountRate = getDiscountRate(item);

  if (discountRate <= 0) {
    return itemPrice;
  }

  if (discountedItemPrice > 0) {
    return discountedItemPrice;
  }

  const discountAmount = (itemPrice * discountRate) / 100;
  const roundedDiscountAmount = Math.round(discountAmount * 100) / 100;

  return itemPrice - roundedDiscountAmount;
};

// Original subtotal before discount.
const calculateOriginalSubTotal = (item, quantityOverride) => {
  const itemPrice = toNumber(item?.itemPrice);
  const qty = toNumber(quantityOverride ?? item?.qty);

  return Number((itemPrice * qty).toFixed(2));
};

// Discounted subtotal after discount.
const calculateDiscountedSubTotal = (item, quantityOverride) => {
  const qty = toNumber(quantityOverride ?? item?.qty);
  const backendDiscountedSubTotal = toNumber(item?.discountedSubTotal);

  if (!hasDiscount(item)) {
    return calculateOriginalSubTotal(item, qty);
  }

  // Use backend subtotal only for the original item qty.
  // If user changed qty locally, recalculate with the new qty.
  if (quantityOverride === undefined && backendDiscountedSubTotal > 0) {
    return backendDiscountedSubTotal;
  }

  return Number((calculateDiscountedItemPrice(item) * qty).toFixed(2));
};

const calculateSavedMoneyPerItem = (item, quantityOverride) => {
  const originalSubTotal = calculateOriginalSubTotal(item, quantityOverride);
  const discountedSubTotal = calculateDiscountedSubTotal(
    item,
    quantityOverride,
  );

  return Number(Math.max(originalSubTotal - discountedSubTotal, 0).toFixed(2));
};

export default function ProductsTable({
  className,
  cartItems,
  onItemRemoved,
  placeOrderPermission,
  updateLocalItems,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [subTotal, setSubTotal] = useState({});
  const [subSavedMoney, setSubSavedMoney] = useState({});
  const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);

  const cartItemsArray = useMemo(() => {
    return Array.isArray(cartItems) ? cartItems : [];
  }, [cartItems]);

  // Total number of items in cart
  useEffect(() => {
    const sumQty = cartItemsArray.reduce((acc, currItem) => {
      return acc + toNumber(currItem?.qty);
    }, 0);

    setTotalNumberOfItems(sumQty);
  }, [cartItemsArray]);

  console.log("cartItemsArray:", cartItemsArray);

  // Calculate total price and saved money.
  // Local updated subtotal is used first. Otherwise, backend/calculated value is used.
  const { totalPrice, totalSavedMoney } = useMemo(() => {
    return cartItemsArray.reduce(
      (totals, item) => {
        const itemId = item?.id;

        const itemSubTotal =
          subTotal[itemId] ?? calculateDiscountedSubTotal(item);

        const itemSavedMoney =
          subSavedMoney[itemId] ?? calculateSavedMoneyPerItem(item);

        return {
          totalPrice: totals.totalPrice + toNumber(itemSubTotal),
          totalSavedMoney: totals.totalSavedMoney + toNumber(itemSavedMoney),
        };
      },
      {
        totalPrice: 0,
        totalSavedMoney: 0,
      },
    );
  }, [cartItemsArray, subTotal, subSavedMoney]);

  // Update quantity in local state.
  const handleQuantityChange = (itemId, newQuantity) => {
    setQuantities((prev) => ({ ...prev, [itemId]: newQuantity }));
  };

  const handleUpdateQty = (
    userid,
    productid,
    itemid,
    selectedQty,
    availableQuantity,
    inputQuantity,
    parentid,
  ) => {
    const restrictedPids200 = [82415, 82416, 82420, 82422];

    const productIdNumber = toNumber(productid);
    const parsedInputQuantity = parseInt(inputQuantity, 10);

    const qty = Number.isFinite(parsedInputQuantity)
      ? parsedInputQuantity
      : toNumber(selectedQty);

    const currentItem = cartItemsArray.find((item) => item.id === itemid);

    if (!currentItem) {
      console.error("Cart item not found:", itemid);
      return;
    }

    // Check if productid is in restrictedPids200.
    if (restrictedPids200.includes(productIdNumber) && qty % 200 !== 0) {
      setDialogMessage(
        "Each carton contains 200 pieces. Please enter the quantity in multiples of 200 (e.g., 200, 400, 600...).",
      );
      setDialogOpen(true);
      setQuantities((prev) => ({ ...prev, [itemid]: 200 }));
      return;
    }

    // Backorder alert.
    if (qty > toNumber(availableQuantity)) {
      setDialogMessage(
        "You can place backorders for any remaining quantities that exceed the current orderable quantity. If you prefer not to place a backorder, kindly adjust the quantity of the item in your order to match the currently available stock.",
      );
      setDialogOpen(true);
    }

    // Local UI update.
    setQuantities((prev) => ({ ...prev, [itemid]: qty }));

    const newSubTotal = calculateDiscountedSubTotal(currentItem, qty);
    const newSavedMoney = calculateSavedMoneyPerItem(currentItem, qty);

    setSubTotal((prev) => ({ ...prev, [itemid]: newSubTotal }));
    setSubSavedMoney((prev) => ({ ...prev, [itemid]: newSavedMoney }));

    // Dispatch guard. Allow ParentId === 0.
    if (!(userid && productid && (parentid || parentid === 0))) return;

    // Prevent duplicate update requests for the same item.
    if (updatingIds.has(itemid)) return;

    setUpdatingIds((prev) => {
      const next = new Set(prev);
      next.add(itemid);
      return next;
    });

    dispatch(
      updateCart({
        UserId: userid,
        Pid: productid,
        Quantity: qty,
        ParentId: parentid,
      }),
    )
      .then(() => {
        return dispatch(getCart());
      })
      .then((data) => {
        const list =
          data?.result ??
          data?.data?.result ??
          data?.payload?.data?.result ??
          [];

        if (Array.isArray(list)) {
          updateLocalItems(list);
        }
      })
      .catch((error) => {
        console.error("Failed to update cart item quantity:", error);
      })
      .finally(() => {
        setUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(itemid);
          return next;
        });
      });
  };

  const handleRemoveItem = async (userid, productid, parentid) => {
    try {
      await dispatch(
        removeItem({
          UserId: userid,
          ProductId: productid,
          ParentId: parentid,
        }),
      );

      const updatedCartResponse = await dispatch(getCart(userid));

      const updatedCartItems =
        updatedCartResponse?.payload?.data?.result ??
        updatedCartResponse?.data?.result ??
        updatedCartResponse?.result ??
        [];

      if (Array.isArray(updatedCartItems)) {
        updateLocalItems(updatedCartItems);
      }

      if (typeof onItemRemoved === "function") {
        onItemRemoved(productid);
      }
    } catch (error) {
      console.error("Failed to remove cart item:", error);
    }
  };

  const goBackProductPage = (collection, styleNumber, selectedColor, fit) => {
    const uppercaseColor = String(selectedColor || "").toUpperCase();

    navigate(`/${collection}/${styleNumber}/${uppercaseColor}/${fit}`);
  };

  const goToShopping = () => {
    navigate("/");
  };

  const goToCheckout = () => {
    navigate("/checkout");
  };

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  return (
    <div className={`w-full ${className || ""}`}>
      <div className="page-title-wrapper w-full h-[100px] py-10">
        <div className="container-x mx-auto">
          <div className="flex items-center">
            <h1 className="shopping-cart-title">
              Shopping Cart - {totalNumberOfItems} item(s)
            </h1>
          </div>
        </div>
      </div>

      <div className="relative w-full overflow-x-auto border border-[#EDEDED]">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <tbody>
            <tr className="text-[13px] font-medium text-black bg-[#F6F6F6] whitespace-nowrap px-2 border-b default-border-bottom uppercase">
              <td className="py-4 pl-10 block whitespace-nowrap min-w-[450px]">
                Product
              </td>
              <td className="py-4 whitespace-nowrap text-center w-[120px]">
                Price
              </td>
              <td className="py-4 whitespace-nowrap text-center w-[120px]">
                Quantity
              </td>
              <td className="py-4 whitespace-nowrap text-center w-[120px]">
                Sub Total
              </td>
              <td className="py-4 whitespace-nowrap text-center w-[120px]">
                Username
              </td>
              <td className="py-4 whitespace-nowrap text-center w-[100px]">
                Admin
              </td>
              <td className="py-4 whitespace-nowrap text-right w-[80px]"></td>
            </tr>

            {cartItemsArray.map((item) => {
              const itemHasDiscount = hasDiscount(item);
              const itemOriginalSubTotal = calculateOriginalSubTotal(item);
              const itemDiscountedSubTotal =
                subTotal[item.id] ?? calculateDiscountedSubTotal(item);
              const itemUnitPrice = toNumber(item.itemPrice);
              const itemDiscountedUnitPrice =
                calculateDiscountedItemPrice(item);

              const productLine = String(item.productLine || "").replace(
                /\s+/g,
                "",
              );
              const styleNumber = String(item.styleNumber || "");
              const colorCode = String(item.colorCode || "");

              return (
                <tr
                  key={item.id}
                  className="bg-white border-b hover:bg-gray-50"
                >
                  <td className="pl-10 py-3 w-[380px]">
                    <div className="flex space-x-6 items-center">
                      <div className="w-[80px] h-[80px] overflow-hidden flex justify-center items-center border border-[#EDEDED]">
                        <img
                          src={`https://maevn-images.s3.us-east-2.amazonaws.com/110x110/${productLine}/${styleNumber}${colorCode}.jpg`}
                          alt="product"
                          className="w-full h-full object-contain"
                          onError={(event) => {
                            event.target.onerror = null;
                            event.target.src =
                              "https://maevn-images.s3.us-east-2.amazonaws.com/swatches/NoImage.jpg";
                          }}
                        />
                      </div>

                      <div
                        className="flex-1 flex flex-col"
                        onClick={() =>
                          goBackProductPage(
                            item.productLine,
                            item.styleNumber,
                            item.colorCode,
                            item.simplifiedFit,
                          )
                        }
                        style={{
                          cursor: "pointer",
                          position: "relative",
                        }}
                      >
                        <p className="font-medium text-[15px] text-qblack">
                          Collection: {item.productLine}, Style Number:{" "}
                          {item.styleNumber}
                        </p>

                        <span className="font-medium text-[15px] text-qblack">
                          Fit: {item.fit} &nbsp;&nbsp;
                          {item.isPreorder ? (
                            <span className="text-qred">Pre-Order!</span>
                          ) : null}
                        </span>

                        <span className="text-[15px] font-normal">
                          Color: {item.color} &nbsp;|&nbsp;Size: {item.size}
                        </span>

                        {Math.abs(Number(item.discountRate || 0)) > 0 && (
                          <span
                            className="mt-[10px]"
                            style={{
                              color: "var(--Colors-Brand-Teal-500, #04ADD1)",
                              fontFamily: "Lato",
                              fontSize: "14px",
                              fontStyle: "normal",
                              fontWeight: "400",
                              lineHeight: "normal",
                            }}
                          >
                            Discount applied
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Item price */}
                  <td className="text-center py-4 px-2">
                    <div className="flex flex-nowrap gap-2 items-center justify-center whitespace-nowrap">
                      <span className="text-[15px] font-normal">
                        ${formatMoney(itemUnitPrice)}
                      </span>

                      {/*
                      {itemHasDiscount ? (
                        <>
                          <span className="text-[15px] font-normal line-through text-gray-500">
                            ${formatMoney(itemUnitPrice)}
                          </span>
                          <span className="text-[15px] font-normal text-red-600">
                            ${formatMoney(itemDiscountedUnitPrice)}
                          </span>
                        </>
                      ) : (
                        <span className="text-[15px] font-normal">
                          ${formatMoney(itemUnitPrice)}
                        </span>
                      )}
                      */}
                    </div>
                  </td>

                  <td className="py-4">
                    <div className="flex flex-col justify-center items-center">
                      <InputQuantityCom
                        key={item.id}
                        itemId={item.id}
                        itemPid={item.pid}
                        quantity={quantities[item.id] ?? item.qty}
                        availableQuantity={item.availableQty}
                        onQuantityChange={handleQuantityChange}
                      />

                      <button
                        type="button"
                        className="text-xs font-semibold text-blue-600 mt-2"
                        disabled={updatingIds.has(item.id)}
                        onClick={() =>
                          handleUpdateQty(
                            item.userId,
                            item.pid,
                            item.id,
                            item.qty,
                            item.availableQty,
                            quantities[item.id] ?? item.qty,
                            item.parentAccountId,
                          )
                        }
                      >
                        {updatingIds.has(item.id)
                          ? "Updating..."
                          : "Update Qty"}
                      </button>
                    </div>
                  </td>

                  {/* Subtotal */}
                  <td className="text-center py-4 px-2">
                    <div className="flex flex-nowrap gap-2 items-center justify-center whitespace-nowrap">
                      <span className="text-[15px] font-normal">
                        ${formatMoney(itemDiscountedSubTotal)}
                      </span>
                      {/*
                      {itemHasDiscount ? (
                        <>
                          <span className="text-[15px] font-normal line-through text-gray-500">
                            ${formatMoney(itemOriginalSubTotal)}
                          </span>
                          <span className="text-[15px] font-normal text-red-600">
                            ${formatMoney(itemDiscountedSubTotal)}
                          </span>
                        </>
                      ) : (
                        <span className="text-[15px] font-normal">
                          ${formatMoney(itemDiscountedSubTotal)}
                        </span>
                      )}
                        */}
                    </div>
                  </td>

                  <td className="text-center py-4 px-2">
                    <div className="flex space-x-1 items-center justify-center">
                      <span className="text-[15px] font-normal text-black-500">
                        {String(item.userName || "").trim()}
                      </span>
                    </div>
                  </td>

                  <td className="text-right py-4">
                    <div className="flex space-x-1 items-center justify-center">
                      <span className="text-[15px] font-normal">
                        {item.parentAccountLoginId}
                      </span>
                    </div>
                  </td>

                  <td className="text-right py-4">
                    <div className="flex space-x-1 items-center justify-center">
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveItem(
                            item.userId,
                            item.pid,
                            item.parentAccountId,
                          )
                        }
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.7 0.3C9.3 -0.1 8.7 -0.1 8.3 0.3L5 3.6L1.7 0.3C1.3 -0.1 0.7 -0.1 0.3 0.3C-0.1 0.7 -0.1 1.3 0.3 1.7L3.6 5L0.3 8.3C-0.1 8.7 -0.1 9.3 0.3 9.7C0.7 10.1 1.3 10.1 1.7 9.7L5 6.4L8.3 9.7C8.7 10.1 9.3 10.1 9.7 9.7C10.1 9.3 10.1 8.7 9.7 8.3L6.4 5L9.7 1.7C10.1 1.3 10.1 0.7 9.7 0.3Z"
                            fill="#AAAAAA"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xs"
          fullWidth
        >
          <StyledDialogContent style={{ paddingTop: 30, paddingBottom: 5 }}>
            <DialogContentText
              id="alert-dialog-description"
              style={{ textAlign: "center" }}
            >
              {dialogMessage}
            </DialogContentText>
          </StyledDialogContent>

          <StyledDialogActions
            style={{ marginTop: "30px", justifyContent: "flex-end" }}
          >
            <PrimaryButton
              width="150px"
              height="40px"
              fontSize="16px"
              onClick={() => setDialogOpen(false)}
            >
              Confirm
            </PrimaryButton>
          </StyledDialogActions>
        </Dialog>
      </div>

      <div className="w-full mt-[23px]">
        <div className="w-full sm:flex justify-between">
          <div className="flex space-x-2.5 items-center"></div>

          <a href="/quickorder" onClick={() => handleClearCart()}>
            <div className="tertiaryButton2">
              <span className="text-sm font-semibold">Clear All</span>
            </div>
          </a>
        </div>

        <div className="w-full mt-[30px] flex sm:justify-end">
          <div className="sm:w-[370px] w-full border border-[#EDEDED] px-[30px] py-[26px]">
            <div className="total mb-6">
              {/* Discount information */}
              {totalSavedMoney > 0 && (
                <div
                  style={{
                    display: "flex",
                    width: "325px",
                    height: "22px",
                    gap: "6px",
                    alignItems: "center",
                    marginBottom: "14px",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M10 15C10.2833 15 10.521 14.904 10.713 14.712C10.905 14.52 11.0007 14.2827 11 14V10C11 9.71667 10.904 9.47933 10.712 9.288C10.52 9.09667 10.2827 9.00067 10 9C9.71733 8.99933 9.48 9.09533 9.288 9.288C9.096 9.48067 9 9.718 9 10V14C9 14.2833 9.096 14.521 9.288 14.713C9.48 14.905 9.71733 15.0007 10 15ZM10 7C10.2833 7 10.521 6.904 10.713 6.712C10.905 6.52 11.0007 6.28267 11 6C10.9993 5.71733 10.9033 5.48 10.712 5.288C10.5207 5.096 10.2833 5 10 5C9.71667 5 9.47933 5.096 9.288 5.288C9.09667 5.48 9.00067 5.71733 9 6C8.99933 6.28267 9.09533 6.52033 9.288 6.713C9.48067 6.90567 9.718 7.00133 10 7ZM10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88334 18.6867 3.825 17.9743 2.925 17.075C2.025 16.1757 1.31267 15.1173 0.788001 13.9C0.263335 12.6827 0.000667933 11.3827 1.26582e-06 10C-0.000665401 8.61733 0.262001 7.31733 0.788001 6.1C1.314 4.88267 2.02633 3.82433 2.925 2.925C3.82367 2.02567 4.882 1.31333 6.1 0.788C7.318 0.262667 8.618 0 10 0C11.382 0 12.682 0.262667 13.9 0.788C15.118 1.31333 16.1763 2.02567 17.075 2.925C17.9737 3.82433 18.6863 4.88267 19.213 6.1C19.7397 7.31733 20.002 8.61733 20 10C19.998 11.3827 19.7353 12.6827 19.212 13.9C18.6887 15.1173 17.9763 16.1757 17.075 17.075C16.1737 17.9743 15.1153 18.687 13.9 19.213C12.6847 19.739 11.3847 20.0013 10 20Z"
                      fill="#04ADD1"
                    />
                  </svg>
                  <span
                    style={{
                      color: "var(--Colors-Brand-Teal-500, #04ADD1)",
                      fontFamily: "Lato",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: "700",
                      lineHeight: "normal",
                      opacity: 0.7,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Discount will be shown on the invoice.
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <p className="text-[14px] font-medium text-qblack">You saved</p>
                <p className="text-[14px] font-medium text-qred">
                  ${formatMoney(totalSavedMoney)}
                </p>
              </div>

              <div className="flex justify-between">
                <p className="text-[18px] font-medium text-qblack">Total</p>
                <p className="text-[18px] font-medium text-qred">
                  ${formatMoney(totalPrice)}
                </p>
              </div>
            </div>

            {placeOrderPermission === 1 && (
              <div>
                <PrimaryButton
                  width="300px"
                  height="50px"
                  fontSize="18px"
                  onClick={goToCheckout}
                  style={{ marginBottom: "10px" }}
                >
                  Go To Checkout
                </PrimaryButton>

                <SecondaryButton
                  width="300px"
                  height="50px"
                  fontSize="18px"
                  onClick={goToShopping}
                >
                  Continue Shopping
                </SecondaryButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
